import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile bot challenge for SPA login forms. The API script
 * is appended to document.head on mount (Vite-friendly: no next/script).
 *
 * `siteKey` comes from each app's `VITE_TURNSTILE_SITEKEY` env. When the
 * site key is empty (dev), the component renders nothing and the parent
 * should fall back to "submit allowed without challenge".
 */
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement | string,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          appearance?: "always" | "execute" | "interaction-only";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function ensureScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_URL}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

export function turnstileEnabled(siteKey: string | undefined | null): boolean {
  return !!siteKey;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  // Los callbacks se guardan en refs y NO entran en las dependencias del
  // efecto que monta el widget. Si entraran, un padre que pase una arrow
  // inline (`onError={() => …}`) le daría una identidad nueva en cada
  // render, y el efecto se limpiaría y volvería a montar el challenge en
  // cada pulsación de tecla del formulario: el widget parpadea sin parar y
  // el token resuelto se pierde. Con refs, el widget se monta una vez por
  // (ready, siteKey) y aun así siempre invoca la versión más reciente.
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    ensureScript().then(() => {
      if (cancelled) return;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  useEffect(() => {
    if (!ready || !ref.current || !window.turnstile) return;
    if (widgetId.current) return;
    widgetId.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      theme: "auto",
      appearance: "always",
      callback: (token: string) => onTokenRef.current(token),
      "error-callback": () => onErrorRef.current?.(),
      "expired-callback": () => onErrorRef.current?.(),
    });
    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [ready, siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} className="flex justify-center my-2" />;
}
